import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { TaxEstimateSchema } from './tax-estimate';

/**
 * POST /api/checkout/shipping-rates-guest
 * Get shipping rates for guest checkout
 */

/**
 * Buyer address schema for guest shipping rate request
 */
export const GuestBuyerAddressSchema = z.object({
  name: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().min(1, 'Postal code is required'),
  country: z.string().optional(),
});

export type GuestBuyerAddress = z.infer<typeof GuestBuyerAddressSchema>;

/**
 * Request schema for guest shipping rates
 */
export const GuestShippingRatesRequestSchema = z.object({
  /** Listing ID to get shipping rates for */
  listingId: z.preprocess(
    (val) => (val === undefined || val === null ? val : String(val)),
    z.string({ error: 'Listing ID is required' }).min(1, 'Listing ID is required')
  ),
  /** Buyer address for shipping destination */
  buyerAddress: GuestBuyerAddressSchema,
  /**
   * For CJ Dropshipping listings only: the CJ variant id (vid) the buyer
   * picked on the product page. Optional — falls back to the cheapest
   * variant in LISTINGS_METADATA.externalData when omitted.
   */
  variantVid: z.string().optional(),
});

export type GuestShippingRatesRequest = z.infer<typeof GuestShippingRatesRequestSchema>;

/**
 * Shipping rate service level schema
 */
export const ShippingServiceLevelSchema = z.object({
  name: z.string(),
  token: z.string(),
  terms: z.string(),
});

export type ShippingServiceLevel = z.infer<typeof ShippingServiceLevelSchema>;

/**
 * Individual shipping rate schema
 */
export const ShippingRateSchema = z.object({
  id: z.string(),
  provider: z.string(),
  servicelevel: ShippingServiceLevelSchema,
  amount: z.string(),
  currency: z.string(),
  estimatedDays: z.number().nullable(),
  durationTerms: z.string().nullable(),
  shipsFrom: z.string().optional(),
  /** The sales tax the order would draw with this rate's freight. */
  tax: TaxEstimateSchema.optional(),
});

export type ShippingRate = z.infer<typeof ShippingRateSchema>;

/**
 * Success response schema for guest shipping rates
 */
export const GuestShippingRatesSuccessSchema = z.object({
  success: z.literal(true),
  /** Array of available shipping rates */
  rates: z.array(ShippingRateSchema),
  /** Shippo shipment ID (if not using default) */
  shipmentId: z.string().optional(),
  /** Whether using default $15 shipping rate */
  isDefault: z.boolean().optional(),
  /** Cross-border order: no carrier rate, the two parties arrange shipping. */
  arrangedSeparately: z.boolean().optional(),
});

export type GuestShippingRatesSuccess = z.infer<typeof GuestShippingRatesSuccessSchema>;

/**
 * Error response schema for guest shipping rates
 */
export const GuestShippingRatesErrorSchema = ErrorResponseSchema;

export type GuestShippingRatesError = z.infer<typeof GuestShippingRatesErrorSchema>;

/**
 * Complete response schema for guest shipping rates
 * Discriminated union of success and error responses
 */
export const GuestShippingRatesResponseSchema = z.discriminatedUnion('success', [
  GuestShippingRatesSuccessSchema,
  GuestShippingRatesErrorSchema,
]);

export type GuestShippingRatesResponse = z.infer<typeof GuestShippingRatesResponseSchema>;
